/* existingBook.js -- THE EXISTING-BOOK OPENING RULES OF THE AE / COMPANY QUOTA STREAMS (W-154, W-123; 2026-10-05).
 *
 * An AE Quota or Company Quota stream in EXISTING mode starts its forecast from three numbers the operator types on the
 * Deferred Revenue page after Step 9 -- none is in any onboarding template:
 *   - the ARR at the close (the Historical ARR grid's close month; for Company Quota also the Opening ARR box),
 *   - the opening deferred-revenue balance (opening_bs_balance).
 * Each rule below has ONE owner here and several readers: the pre-flight's P8 (so Step 9's Module Setup and the Health
 * Check name it), the Deferred Revenue page (the Required mark, the "Use the close balance" action) and, for the AE ARR
 * seed, runFullModel's KPI ARR and the ARR-by-module builder's "estimated" mark. A second copy of any of them is the
 * two-sums defect (CLAUDE.md section 6).
 *
 * MEASURED (diffs/tenant2-triage.txt, cid=36 in memory): a blank Company Quota opening ARR silently drops -77,189.03 of
 * forecast revenue over 9 months (the growth base is 0); a +100,000 typo in the AE opening balance is +100,000 of cash that
 * does not exist; a blank one is -4,434,979.83. A missing AE ARR grid makes the KPI ARR a guess (revenue x 12, +371,461.92).
 */

const r2 = n => Math.round(n * 100) / 100;
const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

/** Is this module's engine output booked? The engine's own gates: AE runs unless disabled (enabled !== false, a NULL is on);
 *  Company Quota runs only when enabled === true (runFullModel / ModelProvider's moduleContext). */
export function moduleRuns(moduleType, settings) {
  if (!settings) return false;
  if (moduleType === 'ae_quota') return settings.enabled !== false;
  if (moduleType === 'company_quota') return settings.enabled === true;
  return false;
}

const existing = s => !!s && s.start_mode === 'existing';

/* -- W-154 (a): THE AE KPI ARR SEED ------------------------------------------------------------------------------------
 * Existing mode: the Historical ARR grid's close month when it is entered (> 0); otherwise revenue x 12 -- an ESTIMATE
 * (the close month's GAAP revenue times twelve). Display only: kpiARR feeds the KPI dashboard and the Scenarios ARR rows,
 * never the income statement, balance sheet or cash flow. runFullModel reads `seed`; the pre-flight and the ARR-by-module
 * builder read `estimated` -- one predicate, so the mark appears exactly when the engine guessed. */
export function aeArrSeed(settings, cutoff) {
  if (!existing(settings)) return { seed: 0, estimated: false };
  const grid = parseFloat((settings.existing_arr || {})[cutoff]);
  if (Number.isFinite(grid) && grid > 0) return { seed: r2(grid), estimated: false };
  return { seed: r2(num((settings.existing_monthly_rev || {})[cutoff]) * 12), estimated: true };
}

/* -- W-154 (b): THE COMPANY QUOTA GROWTH BASE --------------------------------------------------------------------------
 * computeCompanyQuotaDR grows new ACV from opening_arr when growth_type is 'pct' -- all three modes read it: 'compounding'
 * (the first month's prior ARR), 'flat' (opening ARR x pct every month) and 'net_arr' (the first month's prior net ARR).
 * A 'flat' growth TYPE adds typed dollars and never reads the base. The engine reads opening_arr ONLY; the Deferred Revenue
 * page saves the Historical ARR grid's close month into it when the grid holds one. So the rule reads the engine's base:
 * existing mode, running, percentage growth, and opening_arr 0 or blank => the forecast grows from nothing. Never guessed. */
export function cqOpeningArrMissing(settings) {
  if (!existing(settings) || !moduleRuns('company_quota', settings)) return null;
  if ((settings.growth_type || 'flat') !== 'pct') return null;
  if (num(settings.opening_arr) > 0) return null;
  return { opening_arr: num(settings.opening_arr), growth_mode: settings.growth_mode || 'compounding' };
}

/* -- W-123: THE OPENING DEFERRED BALANCE --------------------------------------------------------------------------------
 * The engine starts the stream's deferred roll from opening_bs_balance; the balance sheet's close month is the GL. Any
 * difference books as a deferred-revenue cash-flow change in the first forecast month -- cash that does not exist -- with
 * revenue untouched and the balance sheet balanced. Existing mode only (Zero mode starts from 0 by definition, and a
 * history-carrying Zero stream is already blocked by P8's shape tier). To the cent. A blank opening is 0. */
export function accountCloseBalance(bsLineKey, bsActuals, cutoff) {
  return r2(num(((bsActuals || {})[bsLineKey] || {})[cutoff]));
}
export function openingBookMismatch(moduleType, settings, bsLineKey, bsActuals, cutoff) {
  if (!existing(settings) || !moduleRuns(moduleType, settings) || !bsLineKey || !cutoff) return null;
  const opening = r2(num(settings.opening_bs_balance));
  const close = accountCloseBalance(bsLineKey, bsActuals, cutoff);
  if (opening === close) return null;
  return { opening, close, account: bsLineKey };
}
